import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { toast } from "sonner"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations"
import { resourceAccessService } from "../infrastructure/resource-access-service"
import { AssignResourceAccessRequest, ResourceType } from "../dtos/ResourceAccessDtos"

function assignmentsKey(orgId: string, type: ResourceType, resourceId: string) {
  return ["resource-assignments", orgId, type, resourceId]
}

export function useResourceAssignments(type: ResourceType, resourceId: string) {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useQuery({
    queryKey: assignmentsKey(orgId, type, resourceId),
    queryFn: () => resourceAccessService.getAssignments(orgId, type, resourceId),
    enabled: !!orgId && !!resourceId,
  })
}

export function useAssignResource(type: ResourceType, resourceId: string) {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: ({ userId, request }: { userId: string; request: AssignResourceAccessRequest }) =>
      resourceAccessService.assign(orgId, type, resourceId, userId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentsKey(orgId, type, resourceId) })
      queryClient.invalidateQueries({ queryKey: ["resource-assignees-for-list", orgId, type] })
      toast.success("Access updated")
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        const status = error.response?.status
        if (status === HttpStatusCodes.FORBIDDEN) {
          toast.error("Only the organization owner can manage access")
        } else if (status === HttpStatusCodes.BAD_REQUEST) {
          const code = (error.response?.data as { code?: string } | undefined)?.code
          toast.error(
            code === "InvalidAssignee"
              ? "Only accepted team members can be assigned"
              : "Invalid request",
          )
        } else {
          toast.error("Failed to update access")
        }
      } else {
        toast.error("Failed to update access")
      }
    },
  })
}

export function useRevokeResource(type: ResourceType, resourceId: string) {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (userId: string) => resourceAccessService.revoke(orgId, type, resourceId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentsKey(orgId, type, resourceId) })
      queryClient.invalidateQueries({ queryKey: ["resource-assignees-for-list", orgId, type] })
      toast.success("Access revoked")
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === HttpStatusCodes.FORBIDDEN) {
        toast.error("Only the organization owner can manage access")
      } else {
        toast.error("Failed to revoke access")
      }
    },
  })
}
